import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, format, eachDayOfInterval, isWeekend, parseISO } from 'date-fns';

// Default criteria schema - used when branch hasn't configured custom criteria
// Must match the fallback in TeacherDashboard.jsx and DailyUpdates.jsx
const DEFAULT_CRITERIA_SCHEMA = [
  { key: 'mood', label: 'Nastrój', icon: '😊' },
  { key: 'eating', label: 'Jedzenie', icon: '🍽️' },
  { key: 'nap', label: 'Sen', icon: '😴' }
];

// Helper to get criteria schema with fallback to defaults
const getCriteriaSchema = (branchSettings) => {
  const schema = branchSettings?.daily_update_schema;
  if (schema && schema.length > 0) {
    return schema;
  }
  return DEFAULT_CRITERIA_SCHEMA;
};

// Helper to get date range based on period
const getDateRange = (period) => {
  const today = new Date();
  if (period === 'weekly') {
    return {
      start: startOfWeek(today, { weekStartsOn: 1 }),
      end: endOfWeek(today, { weekStartsOn: 1 })
    };
  }
  // monthly
  return {
    start: startOfMonth(today),
    end: endOfMonth(today)
  };
};

// Helper to format date for SQL
const formatDate = (date) => format(date, 'yyyy-MM-dd');

// Helper to get working days in a range (excludes weekends)
const getWorkingDaysInRange = (start, end) => {
  const days = eachDayOfInterval({ start, end });
  return days.filter(day => !isWeekend(day)).length;
};

// ============================================
// ABSENCE ANALYTICS
// ============================================

/**
 * Get absence trends over time
 * Returns daily absence counts for charting
 */
export const useAbsenceTrends = (branchId, period = 'weekly', groupId = null) => {
  const { start, end } = getDateRange(period);

  return useQuery({
    queryKey: ['analytics', 'absence-trends', branchId, period, groupId],
    queryFn: async () => {
      // Get all children for the branch (or group) using RPC to bypass slow RLS
      const { data: children, error: childrenError } = await supabase.rpc(
        'get_admin_children_for_analytics',
        {
          p_branch_id: branchId,
          p_group_id: groupId || null
        }
      );
      if (childrenError) throw childrenError;

      const childIds = children?.map(c => c.id) || [];
      const totalChildren = childIds.length;

      if (totalChildren === 0) {
        return { data: [], totalChildren: 0 };
      }

      // Get branch closures in the date range (uses RPC that expands date ranges)
      const { data: closures } = await supabase
        .rpc('get_branch_closures_in_range', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(end)
        });

      const closureDates = new Set((closures || []).map(c => c.closure_date));

      // Get parent-reported absences AND teacher-marked absences from daily_updates
      // Use optimized RPC functions that bypass RLS for performance
      const [absencesResult, dailyUpdatesResult] = await Promise.all([
        supabase.rpc('get_admin_absences_for_analytics', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(end),
          p_group_id: groupId || null
        }),
        supabase.rpc('get_admin_daily_updates_for_analytics', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(end),
          p_group_id: groupId || null
        })
      ]);

      if (absencesResult.error) throw absencesResult.error;
      if (dailyUpdatesResult.error) throw dailyUpdatesResult.error;

      const absences = absencesResult.data || [];
      // Filter for teacher-marked absent (attendance = false)
      const teacherMarkedAbsent = (dailyUpdatesResult.data || []).filter(u => u.attendance === false);

      // Group by date (excluding weekends and closure days)
      const byDate = {};
      const days = eachDayOfInterval({ start, end }).filter(day => {
        const dateStr = formatDate(day);
        return !isWeekend(day) && !closureDates.has(dateStr);
      });

      days.forEach(day => {
        const dateStr = formatDate(day);
        byDate[dateStr] = {
          date: dateStr,
          absent: 0,
          planned: 0,
          unplanned: 0,
          _absentChildIds: new Set() // Track which children are already counted as absent
        };
      });

      // First, count parent-reported absences (these take priority)
      absences.forEach(absence => {
        const dateStr = absence.absence_date;
        if (byDate[dateStr]) {
          byDate[dateStr].absent++;
          byDate[dateStr]._absentChildIds.add(absence.child_id);
          if (absence.is_planned) {
            byDate[dateStr].planned++;
          } else {
            byDate[dateStr].unplanned++;
          }
        }
      });

      // Then, add teacher-marked absences (from daily_updates.attendance = false)
      // Only count if not already counted from absences table
      teacherMarkedAbsent.forEach(update => {
        const dateStr = update.date;
        if (byDate[dateStr] && !byDate[dateStr]._absentChildIds.has(update.child_id)) {
          byDate[dateStr].absent++;
          byDate[dateStr].unplanned++; // Teacher-marked = unplanned
          byDate[dateStr]._absentChildIds.add(update.child_id);
        }
      });

      // Calculate percentages (remove internal _absentChildIds from output)
      const data = Object.values(byDate).map(({ _absentChildIds, ...day }) => ({
        ...day,
        absentPercent: totalChildren > 0 ? Math.round((day.absent / totalChildren) * 100) : 0,
        present: totalChildren - day.absent,
        presentPercent: totalChildren > 0 ? Math.round(((totalChildren - day.absent) / totalChildren) * 100) : 0
      }));

      return { data, totalChildren };
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
};

/**
 * Get presence distribution (pie chart data)
 * Returns present, planned absence, unplanned absence counts
 */
export const usePresenceDistribution = (branchId, period = 'weekly', groupId = null, childId = null) => {
  const { start, end } = getDateRange(period);

  return useQuery({
    queryKey: ['analytics', 'presence-distribution', branchId, period, groupId, childId],
    queryFn: async () => {
      // Get children using RPC to bypass slow RLS
      const { data: children, error: childrenError } = await supabase.rpc(
        'get_admin_children_for_analytics',
        {
          p_branch_id: branchId,
          p_group_id: groupId || null
        }
      );
      if (childrenError) throw childrenError;

      // Filter by childId if specified (for single child analytics)
      const filteredChildren = childId
        ? (children || []).filter(c => c.id === childId)
        : (children || []);

      const childIds = filteredChildren.map(c => c.id);

      // Get branch closures in the date range (uses RPC that expands date ranges)
      const { data: closures } = await supabase
        .rpc('get_branch_closures_in_range', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(end)
        });

      const closureDates = new Set((closures || []).map(c => c.closure_date));

      // Calculate working days excluding closures
      const days = eachDayOfInterval({ start, end });
      const workingDays = days.filter(day => {
        const dateStr = formatDate(day);
        return !isWeekend(day) && !closureDates.has(dateStr);
      }).length;

      const totalPossibleDays = childIds.length * workingDays;

      if (totalPossibleDays === 0) {
        return { present: 0, plannedAbsence: 0, unplannedAbsence: 0, total: 0 };
      }

      // Get parent-reported absences AND teacher-marked absences from daily_updates
      // Use optimized RPC functions that bypass RLS for performance
      const [absencesResult, dailyUpdatesResult] = await Promise.all([
        supabase.rpc('get_admin_absences_for_analytics', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(end),
          p_group_id: groupId || null
        }),
        supabase.rpc('get_admin_daily_updates_for_analytics', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(end),
          p_group_id: groupId || null
        })
      ]);

      if (absencesResult.error) throw absencesResult.error;
      if (dailyUpdatesResult.error) throw dailyUpdatesResult.error;

      const absences = absencesResult.data || [];
      // Filter for teacher-marked absent (attendance = false)
      const teacherMarkedAbsent = (dailyUpdatesResult.data || []).filter(u => u.attendance === false);

      // Filter out absences on closure days (they shouldn't count)
      const validAbsences = absences.filter(a => !closureDates.has(a.absence_date));

      // Track which child+date combos are already counted as absent
      const absentChildDates = new Set();
      validAbsences.forEach(a => {
        absentChildDates.add(`${a.child_id}_${a.absence_date}`);
      });

      const plannedAbsence = validAbsences.filter(a => a.is_planned).length;
      let unplannedAbsence = validAbsences.filter(a => !a.is_planned).length;

      // Add teacher-marked absences (from daily_updates.attendance = false)
      // Only count if not already counted from absences table
      teacherMarkedAbsent.forEach(update => {
        if (!closureDates.has(update.date)) {
          const key = `${update.child_id}_${update.date}`;
          if (!absentChildDates.has(key)) {
            unplannedAbsence++; // Teacher-marked = unplanned
            absentChildDates.add(key);
          }
        }
      });

      const totalAbsences = plannedAbsence + unplannedAbsence;
      const present = totalPossibleDays - totalAbsences;

      return {
        present: Math.max(0, present),
        plannedAbsence,
        unplannedAbsence,
        total: totalPossibleDays
      };
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000
  });
};

/**
 * Get/update absence alert threshold for branch
 */
export const useAbsenceAlertThreshold = (branchId) => {
  return useQuery({
    queryKey: ['analytics', 'absence-threshold', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('branches')
        .select('absence_alert_threshold')
        .eq('id', branchId)
        .single();

      if (error) throw error;
      return data?.absence_alert_threshold ?? 20;
    },
    enabled: !!branchId,
    staleTime: 10 * 60 * 1000
  });
};

export const useUpdateAbsenceAlertThreshold = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ branchId, threshold }) => {
      const { error } = await supabase
        .from('branches')
        .update({ absence_alert_threshold: threshold })
        .eq('id', branchId);

      if (error) throw error;
      return threshold;
    },
    onSuccess: (_, { branchId }) => {
      queryClient.invalidateQueries({ queryKey: ['analytics', 'absence-threshold', branchId] });
    }
  });
};

/**
 * Check and create absence alert notification for branch admins
 * Only creates one notification per day per branch to avoid spam
 */
export const useCheckAbsenceAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ branchId, absentPercent, threshold, date }) => {
      // Only alert if threshold exceeded
      if (absentPercent <= threshold) {
        return { alerted: false, reason: 'below_threshold' };
      }

      const alertDate = date || format(new Date(), 'yyyy-MM-dd');

      // Check if notification already exists for this branch and date
      const { data: existingAlert } = await supabase
        .from('notifications')
        .select('id')
        .eq('type', 'low_attendance_alert')
        .eq('metadata->>branch_id', branchId)
        .eq('metadata->>alert_date', alertDate)
        .limit(1);

      if (existingAlert && existingAlert.length > 0) {
        return { alerted: false, reason: 'already_alerted' };
      }

      // Get branch admins for this branch
      const { data: branchAdmins, error: adminsError } = await supabase
        .from('app_users')
        .select('id')
        .eq('primary_role', 'branch_admin')
        .contains('branch_ids', [branchId]);

      if (adminsError || !branchAdmins?.length) {
        return { alerted: false, reason: 'no_admins' };
      }

      // Get branch name for the notification
      const { data: branch } = await supabase
        .from('branches')
        .select('name, display_name')
        .eq('id', branchId)
        .single();

      const branchName = branch?.display_name || branch?.name || 'Oddział';

      // Create notification for each branch admin
      const notifications = branchAdmins.map(admin => ({
        user_id: admin.id,
        type: 'low_attendance_alert',
        title: 'Wysoka absencja',
        message: `Dzisiejsza absencja w ${branchName} wynosi ${Math.round(absentPercent)}%, przekraczając próg ${threshold}%.`,
        priority: 'high',
        read: false,
        metadata: {
          branch_id: branchId,
          alert_date: alertDate,
          absent_percent: absentPercent,
          threshold: threshold
        }
      }));

      const { error: insertError } = await supabase
        .from('notifications')
        .insert(notifications);

      if (insertError) throw insertError;

      return { alerted: true, count: notifications.length };
    },
    onSuccess: (result) => {
      if (result.alerted) {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      }
    }
  });
};

// ============================================
// DZIENNIK (DAILY UPDATE) ANALYTICS
// ============================================

/**
 * Get Dziennik completion trends
 * Returns daily completion percentage over time
 */
export const useDziennikCompletionTrends = (branchId, period = 'weekly', groupId = null) => {
  const { start, end } = getDateRange(period);

  return useQuery({
    queryKey: ['analytics', 'dziennik-trends', branchId, period, groupId],
    queryFn: async () => {
      // Get branch settings with daily_update_schema (which has proper keys)
      const { data: branch, error: branchError } = await supabase
        .from('branches')
        .select('settings')
        .eq('id', branchId)
        .single();

      if (branchError) throw branchError;

      const criteriaKeys = getCriteriaSchema(branch?.settings).map(c => c.key);
      const criteriaCount = criteriaKeys.length;

      // Get children WITH created_at to filter by enrollment date - using RPC to bypass slow RLS
      const { data: children, error: childrenError } = await supabase.rpc(
        'get_admin_children_for_analytics',
        {
          p_branch_id: branchId,
          p_group_id: groupId || null
        }
      );
      if (childrenError) throw childrenError;

      const childIds = children?.map(c => c.id) || [];

      if (childIds.length === 0) {
        return { data: [], criteriaCount };
      }

      // Get branch closures in the date range (uses RPC that expands date ranges)
      const { data: closures } = await supabase
        .rpc('get_branch_closures_in_range', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(end)
        });

      const closureDates = new Set((closures || []).map(c => c.closure_date));

      // Get absences and daily updates using optimized RPC functions
      const [absencesResult, updatesResult] = await Promise.all([
        supabase.rpc('get_admin_absences_for_analytics', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(end),
          p_group_id: groupId || null
        }),
        supabase.rpc('get_admin_daily_updates_for_analytics', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(end),
          p_group_id: groupId || null
        })
      ]);

      if (absencesResult.error) throw absencesResult.error;
      if (updatesResult.error) throw updatesResult.error;

      const absences = absencesResult.data || [];
      const updates = updatesResult.data || [];

      // Group absences by date
      const absencesByDate = {};
      absences.forEach(a => {
        if (!absencesByDate[a.absence_date]) {
          absencesByDate[a.absence_date] = new Set();
        }
        absencesByDate[a.absence_date].add(a.child_id);
      });

      // Group by date and calculate completion (excluding weekends and closure days)
      const days = eachDayOfInterval({ start, end }).filter(day => {
        const dateStr = formatDate(day);
        return !isWeekend(day) && !closureDates.has(dateStr);
      });
      const byDate = {};

      days.forEach(day => {
        const dateStr = formatDate(day);
        // Filter children who existed on this day (created_at <= day)
        const childrenOnThisDay = children.filter(c => {
          const createdDate = c.created_at ? new Date(c.created_at) : new Date(0);
          // Compare just the date portion (child created on or before this day)
          return formatDate(createdDate) <= dateStr;
        });
        const childIdsOnThisDay = new Set(childrenOnThisDay.map(c => c.id));
        // Count absences only for children who existed on this day
        const absentOnThisDay = absencesByDate[dateStr]
          ? [...absencesByDate[dateStr]].filter(id => childIdsOnThisDay.has(id)).length
          : 0;
        byDate[dateStr] = {
          date: dateStr,
          expected: childrenOnThisDay.length - absentOnThisDay,
          complete: 0,
          partial: 0,
          _childIds: childIdsOnThisDay // Store for update matching
        };
      });

      updates?.forEach(update => {
        const dateStr = update.date;
        if (!byDate[dateStr]) return;

        // Only count updates for children who existed on this day
        if (!byDate[dateStr]._childIds.has(update.child_id)) return;

        // Check if complete:
        // - If absent (attendance === false): complete (no criteria needed for absent children)
        // - If present (attendance === true): needs all criteria filled
        const hasAttendance = update.attendance !== null;
        const isAbsent = update.attendance === false;
        const criteriaValues = update.criteria_values || {};
        const filledCriteria = criteriaKeys.filter(key => criteriaValues[key] !== undefined).length;
        const isComplete = isAbsent || (hasAttendance && (criteriaCount === 0 || filledCriteria === criteriaCount));

        if (isComplete) {
          byDate[dateStr].complete++;
        } else if (hasAttendance || filledCriteria > 0) {
          byDate[dateStr].partial++;
        }
      });

      // Calculate percentages (remove internal _childIds field from output)
      const data = Object.values(byDate).map(({ _childIds, ...day }) => ({
        ...day,
        completionPercent: day.expected > 0 ? Math.round((day.complete / day.expected) * 100) : 0
      }));

      return { data, criteriaCount };
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000
  });
};

/**
 * Get average Dziennik completion (pie chart)
 * Uses rolling calculation - only counts days up to today, not future days
 */
export const useDziennikCompletionAverage = (branchId, period = 'weekly', groupId = null) => {
  const { start, end } = getDateRange(period);

  return useQuery({
    queryKey: ['analytics', 'dziennik-average', branchId, period, groupId],
    queryFn: async () => {
      // For rolling calculation, only count up to today (not future days)
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      const effectiveEnd = end > today ? today : end;

      // Similar logic to trends but aggregated
      const { data: branch } = await supabase
        .from('branches')
        .select('settings')
        .eq('id', branchId)
        .single();

      const criteriaKeys = getCriteriaSchema(branch?.settings).map(c => c.key);
      const criteriaCount = criteriaKeys.length;

      // Get children WITH created_at to filter by enrollment date - using RPC to bypass slow RLS
      const { data: children } = await supabase.rpc(
        'get_admin_children_for_analytics',
        {
          p_branch_id: branchId,
          p_group_id: groupId || null
        }
      );
      const childIds = children?.map(c => c.id) || [];

      // Get branch closures in the date range (up to today, uses RPC that expands date ranges)
      const { data: closures } = await supabase
        .rpc('get_branch_closures_in_range', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(effectiveEnd)
        });

      const closureDates = new Set((closures || []).map(c => c.closure_date));

      // Calculate working days excluding closures (only up to today)
      const days = eachDayOfInterval({ start, end: effectiveEnd });
      const workingDaysList = days.filter(day => {
        const dateStr = formatDate(day);
        return !isWeekend(day) && !closureDates.has(dateStr);
      });

      // Get absences and daily updates using optimized RPC functions
      const [absencesResult, updatesResult] = await Promise.all([
        supabase.rpc('get_admin_absences_for_analytics', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(effectiveEnd),
          p_group_id: groupId || null
        }),
        supabase.rpc('get_admin_daily_updates_for_analytics', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(effectiveEnd),
          p_group_id: groupId || null
        })
      ]);

      if (absencesResult.error) throw absencesResult.error;
      if (updatesResult.error) throw updatesResult.error;

      const absences = absencesResult.data || [];
      const updates = updatesResult.data || [];

      // Group absences by date and child
      const absencesByDateChild = new Set();
      absences.forEach(a => {
        absencesByDateChild.add(`${a.child_id}_${a.absence_date}`);
      });

      // Calculate expected per-day, accounting for when each child was added
      let expected = 0;
      workingDaysList.forEach(day => {
        const dateStr = formatDate(day);
        // Count children who existed on this day
        const childrenOnThisDay = children.filter(c => {
          const createdDate = c.created_at ? new Date(c.created_at) : new Date(0);
          return formatDate(createdDate) <= dateStr;
        });
        // Subtract absences for children who existed on this day
        childrenOnThisDay.forEach(c => {
          const key = `${c.id}_${dateStr}`;
          if (!absencesByDateChild.has(key)) {
            expected++;
          }
        });
      });

      if (expected === 0) {
        return { complete: 0, incomplete: 0, total: 0 };
      }

      // Build a map of child creation dates for quick lookup
      const childCreatedMap = {};
      children.forEach(c => {
        childCreatedMap[c.id] = c.created_at ? formatDate(new Date(c.created_at)) : '1970-01-01';
      });

      let complete = 0;
      updates?.forEach(update => {
        // Only count updates for children who existed on that date
        const childCreatedDate = childCreatedMap[update.child_id];
        if (childCreatedDate > update.date) return; // Child didn't exist yet

        // If absent (attendance === false): complete (no criteria needed)
        // If present (attendance === true): needs all criteria filled
        const hasAttendance = update.attendance !== null;
        const isAbsent = update.attendance === false;
        const criteriaValues = update.criteria_values || {};
        const filledCriteria = criteriaKeys.filter(key => criteriaValues[key] !== undefined).length;
        const isComplete = isAbsent || (hasAttendance && (criteriaCount === 0 || filledCriteria === criteriaCount));
        if (isComplete) complete++;
      });

      return {
        complete,
        incomplete: expected - complete,
        total: expected,
        completionPercent: Math.round((complete / expected) * 100)
      };
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000
  });
};

/**
 * Get teacher engagement comparison
 * Returns per-teacher completion rates (branch admin only)
 */
export const useTeacherEngagement = (branchId, groupId = null) => {
  const { start, end } = getDateRange('weekly');

  return useQuery({
    queryKey: ['analytics', 'teacher-engagement', branchId, groupId],
    queryFn: async () => {
      // For rolling calculation, only count up to today
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const effectiveEnd = end > today ? today : end;

      // Get branch settings with criteria schema
      const { data: branch } = await supabase
        .from('branches')
        .select('settings')
        .eq('id', branchId)
        .single();

      const criteriaKeys = getCriteriaSchema(branch?.settings).map(c => c.key);
      const criteriaCount = criteriaKeys.length;

      // Get groups and their teachers
      let groupsQuery = supabase
        .from('groups')
        .select('id, name, main_teacher_id, teacher_ids')
        .eq('branch_id', branchId);

      if (groupId) groupsQuery = groupsQuery.eq('id', groupId);

      const { data: groups } = await groupsQuery;

      // Get children per group WITH created_at to filter by enrollment date - using RPC to bypass slow RLS
      const groupIds = groups?.map(g => g.id) || [];
      const { data: children } = await supabase.rpc(
        'get_children_by_groups_for_analytics',
        {
          p_group_ids: groupIds
        }
      );

      // Store children with their creation dates by group
      const childrenByGroup = {};
      children?.forEach(c => {
        if (!childrenByGroup[c.group_id]) childrenByGroup[c.group_id] = [];
        childrenByGroup[c.group_id].push({
          id: c.id,
          createdAt: c.created_at ? formatDate(new Date(c.created_at)) : '1970-01-01'
        });
      });

      // Get branch closures (uses RPC that expands date ranges)
      const { data: closures } = await supabase
        .rpc('get_branch_closures_in_range', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(effectiveEnd)
        });

      const closureDates = new Set((closures || []).map(c => c.closure_date));

      // Calculate working days (up to today, excluding closures)
      const days = eachDayOfInterval({ start, end: effectiveEnd });
      const workingDaysList = days.filter(day => {
        const dateStr = formatDate(day);
        return !isWeekend(day) && !closureDates.has(dateStr);
      });
      const workingDays = workingDaysList.length;
      const workingDateStrings = new Set(workingDaysList.map(d => formatDate(d)));

      // Get absences and daily updates using optimized RPC functions
      const [absencesResult, updatesResult] = await Promise.all([
        supabase.rpc('get_admin_absences_for_analytics', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(effectiveEnd),
          p_group_id: groupId || null
        }),
        supabase.rpc('get_admin_daily_updates_for_analytics', {
          p_branch_id: branchId,
          p_start_date: formatDate(start),
          p_end_date: formatDate(effectiveEnd),
          p_group_id: groupId || null
        })
      ]);

      if (absencesResult.error) throw absencesResult.error;
      if (updatesResult.error) throw updatesResult.error;

      const absences = absencesResult.data || [];
      const updates = updatesResult.data || [];

      // Group absences by child and date
      const absencesByChildDate = new Set();
      absences.forEach(a => {
        if (workingDateStrings.has(a.absence_date)) {
          absencesByChildDate.add(`${a.child_id}_${a.absence_date}`);
        }
      });

      // Get teacher details
      const teacherIds = new Set();
      groups?.forEach(g => {
        if (g.main_teacher_id) teacherIds.add(g.main_teacher_id);
        g.teacher_ids?.forEach(id => teacherIds.add(id));
      });

      const { data: teachers } = await supabase
        .from('app_users')
        .select('id, first_name, last_name')
        .in('id', Array.from(teacherIds));

      const teacherMap = {};
      teachers?.forEach(t => {
        teacherMap[t.id] = `${t.first_name} ${t.last_name}`;
      });

      // Map teachers to their groups (main teacher gets priority)
      const teacherGroups = {};
      groups?.forEach(g => {
        const mainTeacher = g.main_teacher_id;
        if (mainTeacher) {
          if (!teacherGroups[mainTeacher]) teacherGroups[mainTeacher] = [];
          teacherGroups[mainTeacher].push(g.id);
        }
      });

      // Calculate expected updates per teacher (only for children who existed on each day)
      const teacherStats = {};

      Object.entries(teacherGroups).forEach(([teacherId, groupIdList]) => {
        let expectedUpdates = 0;
        groupIdList.forEach(gId => {
          const groupChildren = childrenByGroup[gId] || [];
          groupChildren.forEach(child => {
            // Count working days where child existed minus absences
            workingDaysList.forEach(day => {
              const dateStr = formatDate(day);
              // Only count if child existed on this day
              if (child.createdAt <= dateStr) {
                const key = `${child.id}_${dateStr}`;
                if (!absencesByChildDate.has(key)) {
                  expectedUpdates++;
                }
              }
            });
          });
        });

        teacherStats[teacherId] = {
          teacherId,
          name: teacherMap[teacherId] || 'Nieznany',
          complete: 0,
          expected: expectedUpdates
        };
      });

      // Build map of child creation dates for filtering updates
      const childCreatedMap = {};
      children?.forEach(c => {
        childCreatedMap[c.id] = c.created_at ? formatDate(new Date(c.created_at)) : '1970-01-01';
      });

      // Count completed updates per teacher (only for children who existed on the update date)
      updates?.forEach(update => {
        const teacherId = update.teacher_id;
        if (!teacherId || !teacherStats[teacherId]) return;

        // Only count if child existed on this date
        const childCreatedDate = childCreatedMap[update.child_id];
        if (childCreatedDate && childCreatedDate > update.date) return;

        // If absent (attendance === false): complete (no criteria needed)
        // If present (attendance === true): needs all criteria filled
        const hasAttendance = update.attendance !== null;
        const isAbsent = update.attendance === false;
        const criteriaValues = update.criteria_values || {};
        const filledCriteria = criteriaKeys.filter(key => criteriaValues[key] !== undefined).length;
        const isComplete = isAbsent || (hasAttendance && (criteriaCount === 0 || filledCriteria === criteriaCount));

        if (isComplete) {
          teacherStats[teacherId].complete++;
        }
      });

      // Calculate percentages based on expected updates
      return Object.values(teacherStats).map(stat => ({
        ...stat,
        total: stat.expected,
        completionPercent: stat.expected > 0 ? Math.round((stat.complete / stat.expected) * 100) : 0
      })).sort((a, b) => b.completionPercent - a.completionPercent);
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000
  });
};

// ============================================
// EVALUATION ANALYTICS
// ============================================

/**
 * Get evaluation scores per group (stacked bar chart)
 * Returns criteria breakdown per group
 */
export const useEvaluationScores = (branchId, period = 'weekly', groupId = null, childId = null) => {
  const { start, end } = getDateRange(period);

  return useQuery({
    queryKey: ['analytics', 'evaluation-scores', branchId, period, groupId, childId],
    queryFn: async () => {
      // Get branch settings with criteria schema (has key and label)
      const { data: branch } = await supabase
        .from('branches')
        .select('settings')
        .eq('id', branchId)
        .single();

      const criteria = getCriteriaSchema(branch?.settings);

      // Get groups
      let groupsQuery = supabase
        .from('groups')
        .select('id, name, color_scheme')
        .eq('branch_id', branchId);

      if (groupId) groupsQuery = groupsQuery.eq('id', groupId);

      const { data: groups } = await groupsQuery;
      const groupIds = groups?.map(g => g.id) || [];
      const groupMap = {};
      groups?.forEach(g => { groupMap[g.id] = g; });

      // Get children using RPC to bypass slow RLS
      const { data: children } = await supabase.rpc(
        'get_children_by_groups_for_analytics',
        {
          p_group_ids: groupIds,
          p_child_id: childId || null
        }
      );

      // Get daily updates using optimized RPC function
      const { data: updates, error: updatesError } = await supabase.rpc('get_admin_daily_updates_for_analytics', {
        p_branch_id: branchId,
        p_start_date: formatDate(start),
        p_end_date: formatDate(end),
        p_group_id: groupId || null
      });

      if (updatesError) throw updatesError;

      // Filter out weekend entries (weekends are non-working days)
      const workingDayUpdates = (updates || []).filter(update => {
        const updateDate = parseISO(update.date);
        return !isWeekend(updateDate);
      });

      // Aggregate by group
      const byGroup = {};
      groupIds.forEach(gId => {
        byGroup[gId] = {
          groupId: gId,
          groupName: groupMap[gId]?.name || 'Nieznana',
          colorScheme: groupMap[gId]?.color_scheme,
          criteria: {}
        };
        criteria.forEach(c => {
          const key = c.key;
          byGroup[gId].criteria[key] = {
            name: c.label || key,
            positive: 0,
            neutral: 0,
            negative: 0,
            total: 0
          };
        });
      });

      workingDayUpdates.forEach(update => {
        const gId = update.group_id;
        if (!byGroup[gId]) return;

        const values = update.criteria_values || {};
        Object.entries(values).forEach(([key, value]) => {
          if (byGroup[gId].criteria[key]) {
            byGroup[gId].criteria[key][value]++;
            byGroup[gId].criteria[key].total++;
          }
        });
      });

      // Format for chart
      return {
        groups: Object.values(byGroup),
        criteria
      };
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000
  });
};

// ============================================
// PHOTO & REACTION ANALYTICS
// ============================================

/**
 * Get new photos per group (bar chart)
 */
export const usePhotoStats = (branchId, period = 'weekly') => {
  const { start, end } = getDateRange(period);

  return useQuery({
    queryKey: ['analytics', 'photo-stats', branchId, period],
    queryFn: async () => {
      // Get groups
      const { data: groups } = await supabase
        .from('groups')
        .select('id, name, color_scheme')
        .eq('branch_id', branchId);

      const groupIds = groups?.map(g => g.id) || [];
      const groupMap = {};
      groups?.forEach(g => { groupMap[g.id] = g; });

      // Get photos in date range
      const { data: photos } = await supabase
        .from('photos')
        .select('group_id')
        .eq('branch_id', branchId)
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString());

      // Count by group
      const counts = {};
      groupIds.forEach(gId => {
        counts[gId] = {
          groupId: gId,
          groupName: groupMap[gId]?.name || 'Nieznana',
          colorScheme: groupMap[gId]?.color_scheme,
          count: 0
        };
      });

      photos?.forEach(p => {
        if (counts[p.group_id]) {
          counts[p.group_id].count++;
        }
      });

      return Object.values(counts).sort((a, b) => b.count - a.count);
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000
  });
};

/**
 * Get new photo reactions per group (bar chart)
 * Uses JSONB reactions format with timestamps
 */
export const usePhotoReactionStats = (branchId, period = 'weekly') => {
  const { start, end } = getDateRange(period);

  return useQuery({
    queryKey: ['analytics', 'reaction-stats', branchId, period],
    queryFn: async () => {
      // Get groups
      const { data: groups } = await supabase
        .from('groups')
        .select('id, name, color_scheme')
        .eq('branch_id', branchId);

      const groupIds = groups?.map(g => g.id) || [];
      const groupMap = {};
      groups?.forEach(g => { groupMap[g.id] = g; });

      // Get photos with reactions
      const { data: photos } = await supabase
        .from('photos')
        .select('group_id, reactions')
        .eq('branch_id', branchId)
        .not('reactions', 'eq', '[]');

      // Count reactions within date range by group
      const counts = {};
      groupIds.forEach(gId => {
        counts[gId] = {
          groupId: gId,
          groupName: groupMap[gId]?.name || 'Nieznana',
          colorScheme: groupMap[gId]?.color_scheme,
          count: 0
        };
      });

      photos?.forEach(p => {
        if (!counts[p.group_id] || !p.reactions) return;

        // Filter reactions by date (JSONB format: [{user_id, created_at}])
        const reactions = p.reactions || [];
        const recentReactions = reactions.filter(r => {
          if (!r.created_at) return false;
          const reactionDate = new Date(r.created_at);
          return reactionDate >= start && reactionDate <= end;
        });

        counts[p.group_id].count += recentReactions.length;
      });

      return Object.values(counts).sort((a, b) => b.count - a.count);
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000
  });
};

// ============================================
// MESSAGE ANALYTICS
// ============================================

/**
 * Get new messages per group (bar chart)
 * Combines announcements + chat messages
 */
export const useMessageStats = (branchId, period = 'monthly') => {
  const { start, end } = getDateRange(period);

  return useQuery({
    queryKey: ['analytics', 'message-stats', branchId, period],
    queryFn: async () => {
      // Get groups
      const { data: groups } = await supabase
        .from('groups')
        .select('id, name, color_scheme')
        .eq('branch_id', branchId);

      const groupIds = groups?.map(g => g.id) || [];
      const groupMap = {};
      groups?.forEach(g => { groupMap[g.id] = g; });

      // Get announcements in date range
      const { data: announcements } = await supabase
        .from('announcements')
        .select('group_id')
        .eq('branch_id', branchId)
        .gte('created_date', start.toISOString())
        .lte('created_date', end.toISOString());

      // Get conversations for this branch
      const { data: conversations } = await supabase
        .from('conversations')
        .select('id, group_id')
        .eq('branch_id', branchId)
        .in('group_id', groupIds);

      const conversationIds = conversations?.map(c => c.id) || [];
      const conversationGroupMap = {};
      conversations?.forEach(c => { conversationGroupMap[c.id] = c.group_id; });

      // Get messages in date range
      let messages = [];
      if (conversationIds.length > 0) {
        const { data: msgs } = await supabase
          .from('messages')
          .select('conversation_id')
          .in('conversation_id', conversationIds)
          .gte('created_at', start.toISOString())
          .lte('created_at', end.toISOString());
        messages = msgs || [];
      }

      // Count by group
      const counts = {};
      groupIds.forEach(gId => {
        counts[gId] = {
          groupId: gId,
          groupName: groupMap[gId]?.name || 'Nieznana',
          colorScheme: groupMap[gId]?.color_scheme,
          announcements: 0,
          messages: 0,
          total: 0
        };
      });

      announcements?.forEach(a => {
        if (counts[a.group_id]) {
          counts[a.group_id].announcements++;
          counts[a.group_id].total++;
        }
      });

      messages?.forEach(m => {
        const groupId = conversationGroupMap[m.conversation_id];
        if (counts[groupId]) {
          counts[groupId].messages++;
          counts[groupId].total++;
        }
      });

      return Object.values(counts).sort((a, b) => b.total - a.total);
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000
  });
};

// ============================================
// OTHER METRICS
// ============================================

/**
 * Get storage usage for branch (in bytes)
 */
export const useStorageUsage = (branchId) => {
  return useQuery({
    queryKey: ['analytics', 'storage-usage', branchId],
    queryFn: async () => {
      const { data: photos, error } = await supabase
        .from('photos')
        .select('file_size')
        .eq('branch_id', branchId);

      if (error) throw error;

      const totalBytes = photos?.reduce((sum, p) => sum + (p.file_size || 0), 0) || 0;
      const totalMB = totalBytes / (1024 * 1024);
      const totalGB = totalBytes / (1024 * 1024 * 1024);

      return {
        bytes: totalBytes,
        mb: Math.round(totalMB * 100) / 100,
        gb: Math.round(totalGB * 100) / 100,
        photoCount: photos?.length || 0
      };
    },
    enabled: !!branchId,
    staleTime: 60 * 60 * 1000 // 1 hour cache
  });
};

/**
 * Get active users (parents who logged in within last 7 days)
 */
export const useActiveUsers = (branchId) => {
  return useQuery({
    queryKey: ['analytics', 'active-users', branchId],
    queryFn: async () => {
      const sevenDaysAgo = subDays(new Date(), 7);

      // Get children for this branch to find their parents - using RPC to bypass slow RLS
      const { data: children } = await supabase.rpc(
        'get_admin_children_for_analytics',
        {
          p_branch_id: branchId,
          p_group_id: null
        }
      );

      // Collect unique parent IDs
      const parentIds = new Set();
      children?.forEach(c => {
        c.parent_ids?.forEach(pid => parentIds.add(pid));
      });

      if (parentIds.size === 0) {
        return { activeCount: 0, totalParents: 0, percentage: 0 };
      }

      // Get app_users with their last_login_at timestamp
      const { data: users } = await supabase
        .from('app_users')
        .select('id, last_login_at')
        .in('id', Array.from(parentIds));

      // Count users who logged in within last 7 days
      const activeUsers = users?.filter(u => {
        if (!u.last_login_at) return false;
        return new Date(u.last_login_at) >= sevenDaysAgo;
      }) || [];

      return {
        activeCount: activeUsers.length,
        totalParents: parentIds.size,
        percentage: parentIds.size > 0 ? Math.round((activeUsers.length / parentIds.size) * 100) : 0
      };
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000
  });
};

// ============================================
// GROUPS LIST (for filters)
// ============================================

/**
 * Get groups for filter dropdown
 */
export const useAnalyticsGroups = (branchId) => {
  return useQuery({
    queryKey: ['analytics', 'groups', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('groups')
        .select('id, name, color_scheme')
        .eq('branch_id', branchId)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: !!branchId,
    staleTime: 10 * 60 * 1000
  });
};

/**
 * Get children for filter dropdown
 */
export const useAnalyticsChildren = (branchId, groupId = null) => {
  return useQuery({
    queryKey: ['analytics', 'children', branchId, groupId],
    queryFn: async () => {
      // Use RPC to bypass slow RLS
      const { data, error } = await supabase.rpc(
        'get_admin_children_for_analytics',
        {
          p_branch_id: branchId,
          p_group_id: groupId || null
        }
      );
      if (error) throw error;
      // Sort by last_name since RPC doesn't support ORDER BY
      return (data || []).sort((a, b) =>
        (a.last_name || '').localeCompare(b.last_name || '')
      );
    },
    enabled: !!branchId,
    staleTime: 10 * 60 * 1000
  });
};

// ============================================
// PHOTO CONSENT ANALYTICS
// ============================================

/**
 * Get teacher to child ratio for the branch
 * Returns the ratio expressed as 1:X
 */
export const useTeacherChildRatio = (branchId) => {
  return useQuery({
    queryKey: ['analytics', 'teacher-child-ratio', branchId],
    queryFn: async () => {
      // Get all children count for the branch - using RPC to bypass slow RLS
      const { data: children, error: childrenError } = await supabase.rpc(
        'get_admin_children_for_analytics',
        {
          p_branch_id: branchId,
          p_group_id: null
        }
      );
      if (childrenError) throw childrenError;

      const childrenCount = children?.length || 0;

      // Get all teachers for the branch
      // Teachers are assigned via groups (main_teacher_id and teacher_ids)
      const { data: groups, error: groupsError } = await supabase
        .from('groups')
        .select('main_teacher_id, teacher_ids')
        .eq('branch_id', branchId);

      if (groupsError) throw groupsError;

      // Collect unique teacher IDs
      const teacherIds = new Set();
      groups?.forEach(g => {
        if (g.main_teacher_id) teacherIds.add(g.main_teacher_id);
        g.teacher_ids?.forEach(id => teacherIds.add(id));
      });

      const teacherCount = teacherIds.size;

      // Calculate ratio (children per teacher)
      const ratio = teacherCount > 0 ? childrenCount / teacherCount : 0;
      const roundedRatio = Math.round(ratio * 10) / 10; // Round to 1 decimal

      return {
        teacherCount,
        childrenCount,
        ratio: roundedRatio,
        ratioDisplay: teacherCount > 0 ? `1:${roundedRatio}` : '-'
      };
    },
    enabled: !!branchId,
    staleTime: 10 * 60 * 1000 // 10 minutes
  });
};

/**
 * Get parents who have NOT given photo consent
 * Returns list of parents with their children
 */
export const useParentsWithoutPhotoConsent = (branchId) => {
  return useQuery({
    queryKey: ['analytics', 'no-photo-consent', branchId],
    queryFn: async () => {
      // Get all children in the branch with their parent relationships
      const { data: children, error: childrenError } = await supabase
        .from('children')
        .select(`
          id,
          first_name,
          last_name,
          group:groups!children_group_id_fkey(name),
          child_parents(
            parent:app_users!child_parents_parent_id_fkey(
              id,
              first_name,
              last_name,
              email
            )
          )
        `)
        .eq('branch_id', branchId)
        .order('last_name');

      if (childrenError) throw childrenError;

      // Extract unique parent IDs
      const parentMap = new Map();
      (children || []).forEach(child => {
        (child.child_parents || []).forEach(cp => {
          if (cp.parent) {
            const parent = cp.parent;
            if (!parentMap.has(parent.id)) {
              parentMap.set(parent.id, {
                ...parent,
                children: []
              });
            }
            parentMap.get(parent.id).children.push({
              id: child.id,
              first_name: child.first_name,
              last_name: child.last_name,
              group_name: child.group?.name
            });
          }
        });
      });

      const parentIds = Array.from(parentMap.keys());

      if (parentIds.length === 0) {
        return { parentsWithoutConsent: [], totalParents: 0 };
      }

      // Get photo consents for these parents
      const { data: consents, error: consentsError } = await supabase
        .from('consents')
        .select('user_id')
        .in('user_id', parentIds)
        .eq('consent_type', 'photo_sharing_internal')
        .eq('granted', true)
        .is('revoked_at', null);

      if (consentsError) throw consentsError;

      // Find parents without consent
      const parentsWithConsent = new Set((consents || []).map(c => c.user_id));
      const parentsWithoutConsent = parentIds
        .filter(id => !parentsWithConsent.has(id))
        .map(id => parentMap.get(id))
        .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));

      return {
        parentsWithoutConsent,
        totalParents: parentIds.length,
        withConsentCount: parentsWithConsent.size
      };
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000
  });
};
